<script>
  import { onDestroy, getContext, createEventDispatcher } from "svelte";
  import { loadImage } from "iso-canvas";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";
  import ProgressBar from "#lib/GenericProgressBar.svelte";
  import { api } from "#src/api";
  import {
    getFiles,
    getFileData,
    getProgress,
    pollUntilComplete,
  } from "#src/extensions/check-document-status.js";
  import { settingTriopsUrl as triopsUrl } from "#src/stores/ui.js";

  export let doc;
  export let group;

  const fileData = getContext("fileData");
  const dispatch = createEventDispatcher();

  let thumbnailKey = crypto.randomUUID();
  let controller = new AbortController();
  let checking = false;

  $: files = getDocFiles(doc);
  $: progress = getProgress(doc, $fileData);
  $: recheck(doc);

  function getDocFiles(doc) {
    if (doc.type === "imported") {
      return getFiles(doc);
    } else if (doc.type === "virtual" && doc.source_document_id) {
      const sourceDoc = $group.documents[doc.source_document_id];
      if (sourceDoc) {
        return getFiles(sourceDoc);
      }
    }
    return [];
  }

  function updateDoc(update) {
    if (update) {
      dispatch("update", update);
    }
    thumbnailKey = crypto.randomUUID();
  }

  function getDoc() {
    return doc;
  }

  async function checkStatus() {
    controller.abort();
    controller = new AbortController();
    checking = true;
    await pollUntilComplete(fileData, getDoc, updateDoc, controller.signal);
    thumbnailKey = crypto.randomUUID();
    checking = false;
  }

  function recheck(doc) {
    if (checking) return;
    checkStatus();
  }

  async function thumbnailSrc() {
    const thumbnail = doc.data.thumbnail;
    if (thumbnail) {
      if (thumbnail.type === "image") {
        const { data, error } = await api.storage
          .from(thumbnail.bucket)
          .createSignedUrl(thumbnail.path, 60 * 60);

        if (error) return null;
        return data.signedUrl;
      } else if (thumbnail.type === "icon") {
        return `/images/${thumbnail.path}`;
      } else if (thumbnail.type === "url") {
        return thumbnail.url;
      } else {
        // iiif thumbnail
        const fd = $fileData[thumbnail.objectName];
        const fn = thumbnail.path.split("/").slice(-1)?.[0];
        const page = fn?.match(/(\d+)\.tiff$/);
        if (fd && page) {
          const state = fd?.status?.[page[1]]?.state;
          if (state !== "completed") {
            return null;
          }
        }

        const path = encodeURIComponent(thumbnail.path);
        const src = `${$triopsUrl}/iiif/2/${thumbnail.bucket}/${path}/full/540,/0/default.jpg`;
        try {
          await loadImage(src);
          return src;
        } catch (e) {
          return null;
        }
      }
    }
  }

  async function fetchData() {
    const s = await Promise.all(files.map((f) => getFileData(f)));
    files.forEach((file, index) => {
      $fileData[file] = s[index];
    });
  }

  onDestroy(() => {
    controller.abort();
  });
</script>

<div class="relative w-full h-full">
  {#await fetchData() then _}
    <div class="w-full h-full flex items-center justify-center bg-white" style="padding:4px;">
      {#if doc.data.thumbnail}
        {#key thumbnailKey}
          {#key doc.data.thumbnail.path}
            {#await thumbnailSrc() then src}
              {#if src}
                <img class="w-full h-full object-cover text-xs" {src} alt={doc.name} />
              {/if}
            {/await}
          {/key}
        {/key}
      {:else}
        <div class="w-full h-full flex flex-col justify-center items-center">
          <div class="animate-spin">
            <SpinnerIcon />
          </div>
        </div>
      {/if}
    </div>
    {#if progress < 0.99999}
      <div class="absolute bottom-0 left-0 w-full">
        <div class="p-2 text-xs text-gray-500">Processing: {Math.round(progress * 100)}%</div>
        <ProgressBar value={progress} max={1} />
      </div>
    {/if}
  {/await}
</div>
